import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config/routes';
import { useAuthContext } from 'context/AuthContext';
import { useStorefrontContext } from 'context/StorefrontContext';
import { resolveSiteName, resolveStoreMediaUrl } from 'utils/themeDisplay';
import CartMiniPreview from 'components/cart/CartMiniPreview';
import SmartSearch from './SmartSearch';
import MegaMenu from './MegaMenu';
import HeaderSelectors from './HeaderSelectors';
import CategoryDrawer from './CategoryDrawer';

const Header: React.FC = () => {
  const { t } = useTranslation('common');
  const { isAuthenticated, customer } = useAuthContext();
  const { theme } = useStorefrontContext();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const siteName = resolveSiteName(theme);
  const isBranded = theme?.headerStyle === 'branded';
  const logoSrc = resolveStoreMediaUrl(theme?.logoUrl);

  const logo = logoSrc ? (
    <Box component="img" src={logoSrc} alt={siteName} sx={{ height: isBranded ? 44 : 36 }} />
  ) : (
    siteName
  );

  const accountLabel = isAuthenticated
    ? t('nav.helloUser', { name: customer?.firstName?.trim() || t('nav.account') })
    : t('nav.helloLogin');

  if (isBranded) {
    return (
      <>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'transparent', color: 'inherit' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Container maxWidth="lg">
              <Toolbar
                disableGutters
                sx={{
                  minHeight: { xs: 64, md: 72 },
                  gap: { xs: 1, md: 2 },
                  py: 1,
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  component={RouterLink}
                  to={ROUTES.HOME}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    maxWidth: { xs: 120, sm: 180, md: 220 },
                    '& img': { maxWidth: '100%', height: 'auto', maxHeight: 44 },
                  }}
                >
                  {logo}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <SmartSearch fullWidth contrast />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0, sm: 0.5 },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    component={RouterLink}
                    to={isAuthenticated ? ROUTES.ACCOUNT : ROUTES.LOGIN}
                    color="inherit"
                    startIcon={<PersonOutlineIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      px: { xs: 0.5, sm: 1 },
                      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
                      '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 22 },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {accountLabel}
                    </Box>
                  </Button>
                  <CartMiniPreview contrast />
                </Box>
              </Toolbar>
            </Container>
          </Box>

          <Box
            sx={{
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Container maxWidth="lg">
              <Toolbar
                disableGutters
                sx={{
                  minHeight: 48,
                  gap: 1,
                  py: 0.5,
                  overflowX: 'auto',
                }}
              >
                <Button
                  color="inherit"
                  startIcon={<MenuIcon />}
                  onClick={() => setDrawerOpen(true)}
                  aria-label={t('nav.openMenu')}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {t('nav.categories')}
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <MegaMenu uppercase />
                </Box>
              </Toolbar>
            </Container>
          </Box>
        </AppBar>
        <CategoryDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      </>
    );
  }

  return (
    <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 2, flexWrap: 'wrap', py: 1 }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to={ROUTES.HOME}
            sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 700, mr: 1 }}
          >
            {logo}
          </Typography>
          <MegaMenu />
          <SmartSearch />
          <HeaderSelectors />
          <Box flexGrow={1} />
          <IconButton
            component={RouterLink}
            to={isAuthenticated ? ROUTES.ACCOUNT : ROUTES.LOGIN}
            aria-label={t('nav.account')}
          >
            <PersonOutlineIcon />
          </IconButton>
          <CartMiniPreview />
          {!isAuthenticated && (
            <Button component={RouterLink} to={ROUTES.LOGIN} variant="outlined" size="small">
              {t('nav.login')}
            </Button>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
